import { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { startOfMonth, subMonths, endOfMonth, addMonths, format, parseISO } from 'date-fns';
import invariant from 'invariant';
import * as R from 'ramda';

import {
	DEFAULT_CALENDAR_VIEW,
	useCalendarSettings,
	useCalendarData,
	useShortcodeSettings,
	useCalendarConfig,
} from '..';
import { parseQueryParams, updateQueryParams } from './utils';

import {
	filterByCity,
	filterByCountry,
	filterByEventTags,
	filterByEventType,
	filterByEventVenue,
	filterBySearchTerm,
	filterByState,
} from './filters';

import type React from 'react';
import type { IEvent } from '..';

export interface Filters {
	selectedEventTypes: string[];
	selectedEventTags: string[];
	selectedEventLocations: string[];
	selectedCountries: string[];
	selectedStates: string[];
	selectedCities: string[];
}

export interface DefaultFilters {
	eventTypes: string[];
	eventTags: string[];
	eventLocations: string[];
	countries: string[];
	states: string[];
	cities: string[];
}

interface FiltersContextProps {
	events: IEvent[];
	filteredEvents: IEvent[];
	defaultFilters: DefaultFilters;
	filters: Filters;
	searchTerm: string;
	currentView: string;
	currentMonth: string;
	filtersInitializedRef: React.MutableRefObject<boolean>;
	setEvents: React.Dispatch<React.SetStateAction<IEvent[]>>;
	setFilters: React.Dispatch<React.SetStateAction<Filters>>;
	setFilteredEvents: React.Dispatch<React.SetStateAction<IEvent[]>>;
	setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
	setCurrentView: React.Dispatch<React.SetStateAction<string>>;
	setCurrentMonth: React.Dispatch<React.SetStateAction<string>>;
	handleApplyFilters: () => void;
}

const FiltersContext = createContext<FiltersContextProps | undefined>(undefined);

const withFilter = (predicate) => R.filter(predicate);

const applyEventFilters = (events, filters, searchTerm, country: string, state: string) =>
	R.pipe(
		withFilter(filterBySearchTerm(searchTerm)),
		withFilter(filterByCountry(filters)),
		withFilter(filterByState(filters, country)),
		withFilter(filterByCity(filters, country, state)),
		withFilter(filterByEventType(filters)),
		withFilter(filterByEventTags(filters)),
		withFilter(filterByEventVenue(filters))
	)(events);

export const FiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { instanceId, dateStart, dateEnd } = useCalendarConfig();
	const { searchTerm: initialSearchTerm, filters: initialFilters } = parseQueryParams(instanceId);
	const { eventsMap, fetchAllEventsInRange, getAllEvents } = useCalendarData();
	const { settings: shortcodeSettings } = useShortcodeSettings();
	const { countries, states } = shortcodeSettings;

	const [defaultFilters, setDefaultFilters] = useState<DefaultFilters>({
		eventTypes: [],
		eventTags: [],
		eventLocations: [],
		countries: [],
		states: [],
		cities: [],
	});
	const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
	const [filters, setFilters] = useState<Filters>(initialFilters);
	const [events, setEvents] = useState<IEvent[] | null>(null);
	const [filteredEvents, setFilteredEvents] = useState<IEvent[] | null>(null);
	const filtersInitializedRef = useRef(false);

	const { settings } = useCalendarSettings();
	const [currentView, setCurrentView] = useState<string>(settings?.defaultView || DEFAULT_CALENDAR_VIEW);
	const [currentMonth, setCurrentMonth] = useState();

	useEffect(() => {
		setCurrentView(shortcodeSettings?.view || settings?.defaultView || DEFAULT_CALENDAR_VIEW);
	}, [settings, shortcodeSettings?.view]);

	useEffect(() => {
		const loadInitialEvents = async () => {
			const now = new Date();
			let start: string;
			let end: string;

			/**
			 * Determine start date for initial event loading
			 * - If dateStart is configured: Use it as the start date
			 * - If no dateStart: Default to 1 month before today
			 */
			if (dateStart) {
				start = dateStart;
			} else {
				start = format(startOfMonth(subMonths(now, 1)), 'yyyy-MM-dd');
			}

			/**
			 * Determine end date for initial event loading
			 * Strategy: Load 2 months of events initially, but respect boundaries
			 *
			 * Scenarios (examples are with respect to 2025-12-05):
			 * 1. Both dateStart & dateEnd configured:
			 *    - Load from dateStart to min(2 months from dateStart, dateEnd)
			 *    Example: dateStart=2025-06-01, dateEnd=2025-12-30
			 *    Result: 2025-06-01 to 2025-07-31 (2 months from start)
			 *
			 * 2. Only dateStart configured:
			 *    - Load 2 months from dateStart
			 *    Example: dateStart=2025-06-01
			 *    Result: 2025-06-01 to 2025-07-31
			 *
			 * 3. Only dateEnd configured:
			 *    - Start from 1 month before today, end at min(1 month after today, dateEnd)
			 *    Example 3a: today=2025-12-05, dateEnd=2025-12-25
			 *    Result: 2025-11-01 to 2025-12-25 (respects dateEnd boundary)
			 *
			 *    Example 3b: today=2025-12-05, dateEnd=2026-02-30
			 *    Result: 2025-11-01 to 2026-01-31 (1 month after today)
			 *
			 * 4. No date range configured:
			 *    - Load from 1 month before today to 1 month after today
			 *    Example: today=2025-12-05
			 *    Result: 2025-11-01 to 2026-01-31
			 */
			if (dateStart) {
				// dateStart is configured: load 2 months from start
				const startDate = parseISO(start);
				const twoMonthsFromStart = format(endOfMonth(addMonths(startDate, 1)), 'yyyy-MM-dd');

				if (dateEnd) {
					// Both configured: respect dateEnd boundary
					end = twoMonthsFromStart <= dateEnd ? twoMonthsFromStart : dateEnd;
				} else {
					// Only dateStart: load 2 months from start
					end = twoMonthsFromStart;
				}
			} else {
				// No dateStart configured: use 1 month after today as default
				const oneMonthAfterToday = format(endOfMonth(addMonths(now, 1)), 'yyyy-MM-dd');

				if (dateEnd) {
					// Only dateEnd configured: respect the boundary
					end = oneMonthAfterToday <= dateEnd ? oneMonthAfterToday : dateEnd;
				} else {
					// No date range: use default
					end = oneMonthAfterToday;
				}
			}

			await fetchAllEventsInRange({ start, end });
		};

		loadInitialEvents();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dateStart, dateEnd]);

	useEffect(() => {
		const events = getAllEvents();
		if (events) {
			setEvents(events);
			if (!filtersInitializedRef.current) {
				setFilteredEvents(events);
			}
		}
	}, [eventsMap, getAllEvents]);

	useEffect(() => {
		if (events?.length > 0) {
			const eventTypes = [...new Set(events.flatMap((event) => event.eventType))].filter((event) => event);
			const eventTags = [...new Set(events.flatMap((event) => event.tags))].filter((event) => event);
			const eventLocations = [...new Set(events.flatMap((event) => event.venue.name))].filter((event) => event);

			const countries = Array.from(new Set(events.map((event) => event.country).filter(Boolean)));
			const states = Array.from(new Set(events.map((event) => event.state).filter(Boolean)));
			const cities = Array.from(new Set(events.map((event) => event.city).filter(Boolean)));

			setDefaultFilters({ eventTypes, eventTags, eventLocations, countries, states, cities });
		}
	}, [events]);

	const handleApplyFilters = useCallback(() => {
		setFilters((filters) => {
			const modifiedFilters = filters;
			setSearchTerm((searchTerm) => {
				const filteredEvents = applyEventFilters(events, modifiedFilters, searchTerm, countries, states);
				setFilteredEvents(filteredEvents as IEvent[]);
				updateQueryParams(searchTerm, filters, instanceId);
				return searchTerm;
			});
			if (modifiedFilters.selectedCountries.length === 0 && !countries) {
				modifiedFilters.selectedCities = [];
				modifiedFilters.selectedStates = [];
			}
			if (modifiedFilters.selectedStates.length === 0 && !states) {
				modifiedFilters.selectedCities = [];
			}
			return modifiedFilters;
		});
	}, [events, countries, states, instanceId, setFilteredEvents, setFilters, setSearchTerm]);

	useEffect(() => {
		if (Object.values(defaultFilters).some((arr) => arr.length > 0)) {
			const hasInitialFilters = initialSearchTerm || Object.values(initialFilters).some((arr) => arr.length > 0);

			if (!filtersInitializedRef.current && hasInitialFilters) {
				handleApplyFilters();
				filtersInitializedRef.current = true;
			}
		}
	}, [events, defaultFilters, initialSearchTerm, initialFilters, handleApplyFilters]);

	useEffect(() => {
		if (filtersInitializedRef.current && events?.length > 0) {
			handleApplyFilters();
		}
	}, [events, handleApplyFilters]);

	const contextValue = useMemo(() => {
		return {
			events: events || [],
			filteredEvents: filteredEvents || [],
			defaultFilters,
			filters,
			searchTerm,
			currentView,
			currentMonth,
			filtersInitializedRef,
			setEvents,
			setFilters,
			setFilteredEvents,
			setSearchTerm,
			setCurrentView,
			setCurrentMonth,
			handleApplyFilters,
		};
	}, [events, filteredEvents, defaultFilters, filters, searchTerm, currentView, currentMonth, handleApplyFilters]);

	return <FiltersContext.Provider value={contextValue}>{children}</FiltersContext.Provider>;
};

export const useFilters = (): FiltersContextProps => {
	const context = useContext(FiltersContext);

	invariant(context, 'useFilters must be used within a FiltersProvider');

	return context;
};
